import React, { useCallback, useEffect, useMemo, useState } from 'react';
import authAxios from '../utils/authAxios';
import PidUPCModal from '../components/PidUPCModal';
import PidTableShell from '../components/PidTableShell';
import { PageWrapper, ContentWrapper } from '../styles/PackageIndexStyles';
import { formatMetricSuffixFromPounds, formatWeightForDisplay } from '../utils/weightString';
import StatusPicker from '../components/StatusPicker';
import DependentsBlockDialog from '../components/DependentsBlockDialog';
import { PID_STATUSES, pidStatusColor, normalizePidStatus, pidStatusRank } from '../utils/pidStatus';
import { changePidStatus } from '../utils/pidStatusApi';

const RETAILERS = ['KRP', 'KRP-PM', 'KRP-MH', 'KRP-RVFH', 'KRP-HLM'];

const PidUPCPage = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [dep, setDep] = useState(null); // { name, dependents } when a UPC inactivation is blocked

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get('/api/pidupc');
      setRows(res.data || []);
    } catch (e) {
      console.error('[GET /api/pidupc]', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleStatus = useCallback(async (row, next) => {
    const id = row.UniqueID;
    const prev = row.Status;
    setRows(rs => rs.map(r => (r.UniqueID === id ? { ...r, Status: next } : r)));
    try {
      const result = await changePidStatus('upc', id, next);
      if (result.blocked) {
        setRows(rs => rs.map(r => (r.UniqueID === id ? { ...r, Status: prev } : r)));
        setDep({ name: `${row.Retailer || 'UPC'} ${row.UPC || ''}`.trim(), dependents: result.dependents });
      }
    } catch (e) {
      console.error('[PidUPC] status change failed', e);
      setRows(rs => rs.map(r => (r.UniqueID === id ? { ...r, Status: prev } : r)));
      alert(e?.response?.data?.error || 'Failed to update status.');
    }
  }, []);

  const handleSaved = () => { setShowAdd(false); fetchAll(); };
  const handleDeleted = () => { setViewRow(null); fetchAll(); };
  const handleUpdated = () => { setViewRow(null); fetchAll(); };

  const columns = useMemo(() => [
    {
      key: 'Status', label: 'Status', interactive: true, cardRole: 'status',
      sortValue: (r) => pidStatusRank(r.Status),
      render: (r) => (
        <StatusPicker
          status={r.Status}
          confirm
          size="sm"
          statuses={PID_STATUSES}
          colorFor={pidStatusColor}
          normalize={normalizePidStatus}
          ariaLabel={`Status for ${r.Retailer || ''} ${r.UPC || ''}`}
          onChange={(next) => handleStatus(r, next)}
        />
      ),
    },
    { key: 'Retailer', label: 'Retailer' },
    { key: 'Commodity', label: 'Commodity' },
    {
      key: 'Weight', label: 'Count / Weight',
      sortValue: (r) => Number.parseFloat(String(r.Weight ?? '')) || 0,
      render: (r) => (
        <>
          {formatWeightForDisplay(r.Weight)}
          {formatMetricSuffixFromPounds(r.Weight) ? (
            <span style={{ color: 'var(--shadow-a45)', fontWeight: 700 }}>
              {formatMetricSuffixFromPounds(r.Weight)}
            </span>
          ) : null}
        </>
      ),
    },
    { key: 'UPC', label: 'UPC', emphasis: true, cardRole: 'title' },
  ], [handleStatus]);

  const searchText = useCallback((r) => [
    r.Status, r.Retailer, r.Commodity,
    formatWeightForDisplay(r.Weight), formatMetricSuffixFromPounds(r.Weight), r.UPC,
  ].filter(Boolean).join(' '), []);

  return (
    <PageWrapper>
      <ContentWrapper>
        <PidTableShell
          title="PID UPC"
          columns={columns}
          rows={rows}
          rowKey={(r) => r.UniqueID}
          searchText={searchText}
          defaultSort={{ key: 'Retailer', dir: 'asc' }}
          onAdd={() => setShowAdd(true)}
          onRowClick={(r) => setViewRow(r)}
          rowAriaLabel={(r) => `Edit UPC ${r.UPC || ''} (${r.Retailer || ''})`}
          loading={loading}
          loadingText="Loading UPCs..."
          emptyText="No PID UPC records found."
        />
      </ContentWrapper>

      {showAdd && (
        <PidUPCModal mode="create" retailers={RETAILERS} onClose={() => setShowAdd(false)} onSaved={handleSaved} />
      )}

      {viewRow && (
        <PidUPCModal mode="edit" data={viewRow} onClose={() => setViewRow(null)} onDeleted={handleDeleted} onSaved={handleUpdated} />
      )}

      {dep && (
        <DependentsBlockDialog
          open
          scope="pid"
          subjectName={dep.name}
          dependents={dep.dependents}
          onClose={() => setDep(null)}
        />
      )}
    </PageWrapper>
  );
};

export default PidUPCPage;
